import { action, observable } from 'mobx';
import { Helper } from '../../../Helper';
import { IContentMap } from '../../../interfaces/IContentMap';
import { IInstance } from '../../../interfaces/IInstance';
import { ITargetingApplications } from '../../../interfaces/ITargetingApplications';
import { ITreeLinkNode } from '../../../interfaces/ITreeLinkNode';
import { IFontSlots } from '../../../interfaces/IFontSlots';

export class ContentMapStore {

  // //////////////////////////////// Component properties //////////////////////////////////

  @observable public environment: "Development" | "Test" | "Production";
  @observable public token: string;
  @observable public tenant: string;
  @observable public highlightColor: string;
  @observable public icon: string;
  @observable public instance: IInstance;
  @observable public title: string;
  @observable public isMobile: boolean;
  @observable public hasDarkBackground: boolean;
  @observable public styles: React.CSSProperties;
  @observable public inEditMode: boolean;
  @observable public contentMap: IContentMap;
  @observable public originalContentMap: string;
  @observable public saving: boolean;
  @observable public applicationSettings: ITargetingApplications;
  @observable public resetRowHeights: boolean;
  @observable public content: JSX.Element[];
  @observable public disableAddButton: boolean;
  @observable public openAllLinksExternally: boolean;
  @observable public showDesignEditPanel: boolean;
  @observable public showForgotToSavePopup: boolean;
  @observable public extensionChanged: boolean = false;
  @observable public returnPageUrl: boolean;
  @observable public isExtension: boolean;
  @observable public fontSlots: IFontSlots;


  @action public setEnvironment(environment: "Development" | "Test" | "Production"): void {
    this.environment = environment;
  }

  @action public setToken(token: string): void {
    this.token = token;
  }

  @action public setTenant(tenant: string): void {
    this.tenant = tenant;
  }

  @action public setHighlightColor(highlightColor: string): void {
    this.highlightColor = highlightColor;
  }

  @action public setIcon(icon: string): void {
    this.icon = icon;
  }

  @action public setInstance(instance: IInstance): void {
    this.instance = instance;
  }

  @action public setTitle(title: string): void {
    this.title = title;
  }

  @action public setIsMobile(isMobile: boolean): void {
    this.isMobile = isMobile;
  }

  @action public setHasDarkBackground(hasDarkBackground: boolean): void {
    this.hasDarkBackground = hasDarkBackground;
  }

  @action public setStyles(styles: React.CSSProperties): void {
    this.styles = styles;
  }

  @action public setInEditMode(inEditMode: boolean): void {
    this.inEditMode = inEditMode;
  }

  @action public setContentMap(contentMap: IContentMap): void {
    if (this.targetedPreviewIds) {
      const treeLinkNodes = this.checkNodesForTargetedPreview(contentMap.configuration.treeLinkNodes);
      contentMap.configuration.treeLinkNodes = treeLinkNodes;
      this.contentMap = contentMap;
    } else {
      this.contentMap = contentMap;
    }
  }

  @action private checkNodesForTargetedPreview(treeLinkNodes): ITreeLinkNode[] {
    const targetedPreviewNodes = [];
    treeLinkNodes.forEach((treeLinkNode: ITreeLinkNode) => {
      treeLinkNode.children = this.checkNodesForTargetedPreview(treeLinkNode.children);
      if (treeLinkNode.targeting.enabled) {
        if (this.checktargetedPreviewIds(treeLinkNode)) {
          targetedPreviewNodes.push(treeLinkNode);
        }
      } else {
        targetedPreviewNodes.push(treeLinkNode);
      }
    });
    return targetedPreviewNodes;
  }

  @action public setOriginalContentMap(originalContentMap: string): void {
    this.originalContentMap = originalContentMap;
  }

  @action public setSaving(saving: boolean): void {
    this.saving = saving;
  }

  @action public setApplicationSettings(applicationSettings: ITargetingApplications): void {
    this.applicationSettings = applicationSettings;
  }

  @action public setResetRowHeights(resetRowHeights: boolean): void {
    this.resetRowHeights = resetRowHeights;
  }

  @action public setContent(content: JSX.Element[]): void {
    this.content = content;
  }

  @action public setDisableAddButton(disableAddButton: boolean): void {
    this.disableAddButton = disableAddButton;
  }

  @action public setOpenAllLinksExternally(openAllLinksExternally: boolean): void {
    this.openAllLinksExternally = openAllLinksExternally;
  }

  @action public setShowDesignEditPanel(showDesignEditPanel: boolean): void {
    this.showDesignEditPanel = showDesignEditPanel;
  }

  @action public setShowForgotToSavePopup(showForgotToSavePopup: boolean): void {
    this.showForgotToSavePopup = showForgotToSavePopup;
  }

  @action public setExtensionChanged(extensionChanged: boolean): void {
    this.extensionChanged = extensionChanged;
  }

  @action public removeTree(node: ITreeLinkNode): void {
    const updatedTreeLinkNodes = this.contentMap.configuration.treeLinkNodes.filter(x => x.config.id !== node.config.id);
    this.contentMap.configuration.treeLinkNodes = updatedTreeLinkNodes;
  }

  @action public setReturnPageUrl(returnPageUrl: boolean): void {
    this.returnPageUrl = returnPageUrl;
  }

  @action public addNew() {
    const treeLink: ITreeLinkNode = {
      type: "Text",
      config: {
        id: Helper.getRandomStringKey(),
        title: undefined,
        link: undefined,
        openNewTab: true,
        nodeLevel: 0,
        isContextMenuExpanded: false,
      },
      children: [],
      targeting: {
        enabled: false
      }
    };
    this.contentMap.configuration.treeLinkNodes.push(treeLink);
  }

  ////////////////////////////////////// Drag'n'drop //////////////////////////////////////

  // @observable public isDraggingItemWithIndex: number;
  @observable public draggedOverItemIndex: number;
  @observable public draggedTreeLinkNode: ITreeLinkNode;
  @observable public dropAreaTreeLinkNode: ITreeLinkNode;
  @observable public dropAreaTreeLinkGroup: ITreeLinkNode;
  @observable public dropAsFirst: boolean;

  @action public setDraggedOverItemIndex(draggedOverItemIndex: number): void {
    this.draggedOverItemIndex = draggedOverItemIndex;
  }

  @action public setDraggedTreeLinkNode(draggedTreeLinkNode: ITreeLinkNode): void {
    this.draggedTreeLinkNode = draggedTreeLinkNode;
  }

  @action public setDropAreaTreeLinkNode(dropAreaTreeLinkNode: ITreeLinkNode): void {
    this.dropAreaTreeLinkNode = dropAreaTreeLinkNode;
  }

  @action public setDropAreaTreeLinkGroup(dropAreaTreeLinkGroup: ITreeLinkNode): void {
    this.dropAreaTreeLinkGroup = dropAreaTreeLinkGroup;
  }

  @action public setDropAsFirst(dropAsFirst: boolean): void {
    this.dropAsFirst = dropAsFirst;
  }

  @action private findNode(node: ITreeLinkNode, idNode: string): ITreeLinkNode {
    let selectedNode;
    if (node.config.id === idNode) {
      selectedNode = node;
    } else if (node.children && node.children.length > 0) {
      for (let i = 0; node.children.length > i; i++) {
        selectedNode = this.findNode(node.children[i], idNode);
        if (selectedNode) {
          break;
        }
      }
    }
    return selectedNode;
  }

  @action public drop(): void {
    if (this.dropAreaTreeLinkNode && this.draggedTreeLinkNode.config.id !== this.dropAreaTreeLinkNode.config.id) {
      const copyOfDraggedNode = Object.assign({}, this.draggedTreeLinkNode);
      this.removeDraggedTreeLinkNode();
      let parentNode: ITreeLinkNode;
      let indexToAdd: number;
      if (this.dropAreaTreeLinkNode.config.nodeLevel === 0) {
        parentNode = this.dropAreaTreeLinkNode;
      } else {
        this.contentMap.configuration.treeLinkNodes.forEach((treeLinkNode: ITreeLinkNode) => {
          const tempNode = this.findNode(treeLinkNode, this.dropAreaTreeLinkNode.config.parent);
          if (tempNode) {
            parentNode = tempNode;
          }
        });
        if (parentNode) {
          parentNode.children.forEach((child: ITreeLinkNode, index) => {
            if (child.config.id === this.dropAreaTreeLinkNode.config.id) {
              indexToAdd = index + 1;
            }
          });
        }
      }
      if (copyOfDraggedNode && parentNode) {
        copyOfDraggedNode.config.parent = parentNode.config.id;
        copyOfDraggedNode.config.nodeLevel = parentNode.config.nodeLevel + 1;
        if (parentNode.children === undefined) {
          parentNode.children = [];
        }
        if (this.dropAreaTreeLinkNode.config.nodeLevel === 0) {
          parentNode.children.unshift(copyOfDraggedNode);
        } else {
          parentNode.children.splice(indexToAdd, 0, copyOfDraggedNode);
        }
      }
    }
    if (this.dropAreaTreeLinkGroup && this.dropAreaTreeLinkGroup.config.id !== this.draggedTreeLinkNode.config.id) {
      let currentIndex;
      let newIndex;
      this.contentMap.configuration.treeLinkNodes.forEach((child: ITreeLinkNode, index) => {
        if (this.draggedTreeLinkNode.config.id === child.config.id) {
          currentIndex = index;
        }
        index++;
      });
      const copyOfDraggedNode = Object.assign({}, this.draggedTreeLinkNode);
      this.contentMap.configuration.treeLinkNodes.splice(currentIndex, 1);
      this.contentMap.configuration.treeLinkNodes.forEach((child: ITreeLinkNode, index) => {
        if (this.dropAreaTreeLinkGroup.config.id === child.config.id) {
          newIndex = index;
        }
        index++;
      });
      if (this.dropAsFirst) {
        this.contentMap.configuration.treeLinkNodes.unshift(copyOfDraggedNode);
      } else {
        this.contentMap.configuration.treeLinkNodes.splice(newIndex + 1, 0, copyOfDraggedNode);
      }
    }
    this.draggedOverItemIndex = undefined;
    this.dropAreaTreeLinkNode = undefined;
    this.draggedTreeLinkNode = undefined;
    this.dropAreaTreeLinkGroup = undefined;
  }

  private removeDraggedTreeLinkNode(): void {
    let parentNode: ITreeLinkNode;
    console.log(this.draggedTreeLinkNode.config.parent);
    this.contentMap.configuration.treeLinkNodes.forEach((treeLinkNode: ITreeLinkNode) => {
      const tempNode = this.findNode(treeLinkNode, this.draggedTreeLinkNode.config.parent);
      if (tempNode) {
        parentNode = tempNode;
      }
    });
    let indexToDelete: number;
    parentNode.children.forEach((child: ITreeLinkNode, index) => {
      if (child.config.id === this.draggedTreeLinkNode.config.id) {
        indexToDelete = index;
      }
    });
    parentNode.children.splice(indexToDelete, 1);
  }

  @action public addDroppableItemAtIndex(index: number) {
    console.log(index);
    // this.items.splice(index, 0, );
  }

  // @action public isDraggingOverItemWithIndex(index: number) {
  //   if (index !== this.isDraggingItemWithIndex) {
  //     this.contentMap.configuration.treeLinkNodes = this.swapArrayElements(this.contentMap.configuration.treeLinkNodes, index, this.isDraggingItemWithIndex);
  //     this.isDraggingItemWithIndex = index;
  //   }
  // }

  // private swapArrayElements(a, x, y): any {
  //   if (a.length === 1) {
  //     return a;
  //   }
  //   a.splice(y, 1, a.splice(x, 1, a[y])[0]);
  //   return a;
  // }

  @observable public targetedPreviewIds: number[];

  @action public checktargetedPreviewIds(element): boolean {
    let isPartOftargetedPreviewIds = false;
    if (this.targetedPreviewIds && this.targetedPreviewIds.length > 0 && element?.targeting?.enabled && element?.targeting?.targetingGroups?.length > 0) {
      element?.targeting?.targetingGroups.forEach((targetingGroup: string) => {
        this.targetedPreviewIds.forEach((targetedPreviewId: number) => {
          if (`${targetingGroup}` === `${targetedPreviewId}`) {
            isPartOftargetedPreviewIds = true;
          }
        });
      })
    } else {
      isPartOftargetedPreviewIds = true;
    }
    return isPartOftargetedPreviewIds;
  }
} 